import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import { getChannelDefaults } from '@/components/pages/job-posting/channel-defaults';
import { useBudgetType } from '@/hooks/use-budget-type';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { getChannelName } from '@/utils/channel-names';
import { durationInDays, today } from '@/utils/date-util';

interface UseEditCampaignSchemaParams {
  channelSlug?: string;
  budgetSpent?: number | null;
  isCompleted?: boolean;
}

const campaignBaseSchema = z.object({
  starts_at: z.string().optional(),
  end_at: z.string().nullable().optional(),
  budget: z.number().nullable().optional(),
  budget_type: z.enum(['daily', 'weekly', 'monthly', 'lifetime']).optional(),
  end_at_type: z.enum(['fixed', 'target']).nullable().optional(),
  expected_cpa: z.number().nullable().optional(),
  expected_conversion: z.number().nullable().optional(),
  total_budget: z.number().optional(),
  target_applicants: z.number().nullable().optional(),
  target_hires: z.number().nullable().optional(),
  reach_campaign: z
    .object({
      end_at: z.string().nullable().optional(),
      budget: z.number().nullable().optional(),
      expected_conversion: z.number().nullable().optional(),
      expected_cpa: z.number().nullable().optional(),
      reach_campaign_id: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  is_editable: z.boolean().optional(),
});

type CampaignSchemaData = z.infer<typeof campaignBaseSchema>;

/**
 * Builds a Zod schema for the edit-campaign form.
 *
 * Mirrors the validation rules from the job-posting channel schema
 * (minBudget, minDuration, end-date-after-start-date) while also
 * enforcing that the lifetime budget cannot drop below already-spent.
 */
export function useEditCampaignSchema({
  channelSlug,
  budgetSpent,
  isCompleted = false,
}: UseEditCampaignSchemaParams) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { calculateTotalBudget } = useBudgetType();

  const channelDefaults = channelSlug
    ? getChannelDefaults(channelSlug)
    : undefined;
  const minBudget = channelDefaults?.minBudget;
  const minDuration =
    channelDefaults?.minDuration ?? channelDefaults?.duration ?? 1;
  const channelName = channelSlug ? getChannelName(channelSlug) : '';
  const actualBudgetSpent = budgetSpent ?? 0;

  return useMemo(
    () =>
      campaignBaseSchema.superRefine((data: CampaignSchemaData, ctx) => {
        if (isCompleted) return;

        const { budget, budget_type: budgetType } = data;
        const isLifetime = !budgetType || budgetType === 'lifetime';

        if (
          isLifetime &&
          budget &&
          actualBudgetSpent > 0 &&
          budget < actualBudgetSpent
        ) {
          ctx.addIssue({
            code: 'custom',
            message: intl.formatMessage({
              defaultMessage: 'Total budget cannot be less than already spent',
              description:
                'Validation: lifetime budget must cover existing spend to date',
            }),
            path: ['budget'],
          });
        }

        if (budget && minBudget) {
          const { starts_at: startsAt, end_at: endAt } = data;
          let effectiveTotalBudget: number;

          if (isLifetime || !startsAt || !endAt) {
            effectiveTotalBudget = budget;
          } else {
            const campaignStart = new Date(startsAt);
            const remainingStartDate =
              campaignStart > today ? startsAt : today.toISOString();

            effectiveTotalBudget = Math.round(
              actualBudgetSpent +
                calculateTotalBudget({
                  budgetType: budgetType ?? 'lifetime',
                  budgetPerUnit: budget,
                  startDate: remainingStartDate,
                  endDate: endAt,
                }),
            );
          }

          if (effectiveTotalBudget < minBudget) {
            ctx.addIssue({
              code: 'custom',
              message: intl.formatMessage(
                {
                  defaultMessage:
                    'Total campaign budget of {totalBudget} is less than the minimum required budget of {minBudget} for {channelName}',
                  description:
                    'Error when the computed total campaign budget (spend + projected) falls below the channel minimum',
                },
                {
                  totalBudget: formatCurrency(effectiveTotalBudget),
                  channelName,
                  minBudget: formatCurrency(minBudget),
                },
              ),
              path: ['budget'],
            });
          }
        }

        const { starts_at: startsAt, end_at: endAt } = data;

        if (startsAt && endAt) {
          const startDate = new Date(startsAt);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(endAt);
          endDate.setHours(0, 0, 0, 0);

          if (endDate <= startDate) {
            ctx.addIssue({
              code: 'custom',
              message: intl.formatMessage({
                defaultMessage: 'End date must be after start date',
                description:
                  'Validation: campaign end date cannot be on or before the start date',
              }),
              path: ['end_at'],
            });
            return;
          }

          const duration = durationInDays(startsAt, endAt);
          if (minDuration && duration < minDuration) {
            ctx.addIssue({
              code: 'custom',
              message: intl.formatMessage(
                {
                  defaultMessage:
                    'Minimum campaign duration is {minDuration} days',
                  description:
                    'Validation: campaign must run for at least the channel-required number of days',
                },
                { minDuration },
              ),
              path: ['end_at'],
            });
          }
        }
      }),
    [
      intl,
      formatCurrency,
      calculateTotalBudget,
      minBudget,
      minDuration,
      channelName,
      actualBudgetSpent,
      isCompleted,
    ],
  );
}
